<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card" style="margin-top: 50px">
        <Header />
        <div class="card-body">
          <div class="filter-panel mb-4">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h5 class="mb-1 font-weight-bold">Employee Filter</h5>
                <small class="text-muted">Narrow certificate submissions by employee organization data.</small>
              </div>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="filterUsers = { company: null, division: null, department: null, position: null, employee: null, search: null }; filters.nik = null; loadDataUsers(); loadData();">
                Clear Employee
              </button>
            </div>
            <div class="row g-3">
              <div class="col-md-4">
                <label class="filter-label">Company</label>
                <v-select
                  :options="displayFilterOptions.company"
                  v-model="filterUsers.company"
                  placeholder="Select Company"
                  :clearable="true"
                />
              </div>

              <div class="col-md-4">
                <label class="filter-label">Division</label>
                <v-select
                  :options="displayFilterOptions.division"
                  v-model="filterUsers.division"
                  placeholder="Select Division"
                  :clearable="true"
                />
              </div>

              <div class="col-md-4">
                <label class="filter-label">Department</label>
                <v-select
                  :options="displayFilterOptions.department"
                  v-model="filterUsers.department"
                  placeholder="Select Department"
                  :clearable="true"
                />
              </div>

              <div class="col-md-4">
                <label class="filter-label">Position</label>
                <v-select
                  :options="displayFilterOptions.position"
                  v-model="filterUsers.position"
                  placeholder="Select Position"
                  :clearable="true"
                />
              </div>

              <div class="col-md-8">
                <label class="filter-label">Employee</label>
                <v-select
                  :options="displayFilterOptions.employee"
                  v-model="filterUsers.employee"
                  :reduce="option => option.nik"
                  label="fullname"
                  placeholder="Select Employee"
                  :clearable="true"
                  :filterable="false"
                  @search="onEmployeeSearch"
                >
                  <template #no-options="{ search, searching }">
                    <span v-if="!search">Type at least 2 characters to search employee.</span>
                    <span v-else-if="search.length < 2">Type at least 2 characters to search employee.</span>
                    <span v-else-if="searching">Searching...</span>
                    <span v-else>No BIB employee found.</span>
                  </template>
                  <template #option="option">
                    <div>{{ option.fullname }} - {{ option.nik }}</div>
                  </template>
                  <template #selected-option="option">
                    <div>{{ option.fullname }} - {{ option.nik }}</div>
                  </template>
                </v-select>
              </div>
            </div>
          </div>

          <Summary
            :isLoadingSummary="isLoadingSummary"
            :summaryData="summaryIdpLearning"
            :approvalReport="certificateApprovalReport"
          />

          <div class="row align-items-center justify-content-between mb-4">
            <div class="col-md-2">
              <h4 class="mb-0 font-weight-bold">Course List</h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex justify-content-between align-items-center gap-2">
                <v-select
                  :options="statusOptions"
                  v-model="filters.status"
                  :reduce="option => option.value"
                  label="label"
                  class="w-50"
                  placeholder="Status"
                  :clearable="true"
                  @input="filters.page = 1; updateActiveFilterCount(); loadData();"
                />
                <div class="w-100">
                  <b-form-input
                    class="w-full"
                    v-model="filters.search"
                    placeholder="Search Here..."
                    @keyup.enter="onSearch"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    class="btn btn-outline-danger position-relative d-flex flex-row gap-1"
                    @click="showFilterModal"
                    style="height: 27.88px"
                  >
                    <span class="fe fe-sliders"></span> Filter
                    <span
                      v-if="activeFiltersCount > 0"
                      class="badge bg-danger position-absolute top-0 start-100 translate-middle rounded-pill"
                      style="font-size: 0.65rem; padding: 0.2rem 0.4rem"
                    >
                      {{ activeFiltersCount }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeFiltersCount > 0" class="mb-3">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <small class="text-muted">Active Filters:</small>

              <span v-if="filters.training_scope" class="badge bg-light text-dark border">
                Training Scope: {{ filters.training_scope }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_scope')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.platform" class="badge bg-light text-dark border">
                Platform: {{ filters.platform }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('platform')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_method_id" class="badge bg-light text-dark border">
                Training Method ID: {{ filters.training_method_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_method_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_type" class="badge bg-light text-dark border">
                Training Type: {{ filters.training_type }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_type')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.competency_id" class="badge bg-light text-dark border">
                Competency: {{ filters.competency_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('competency_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.status" class="badge bg-light text-dark border">
                Status: {{ statusOptions.find(option => option.value === filters.status)?.label || filters.status }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('status')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <button type="button" class="btn btn-sm btn-link text-danger p-0 ms-2" @click="resetFilters">Clear All</button>
            </div>
          </div>

          <CertificateReviewTable
            :rows="idpLearnings.data"
            :total="idpLearnings.totalItems"
            :showing-from="showingFrom"
            :showing-to="showingTo"
            :page="filters.page"
            :page-size="filters.pageSize"
            @sort-change="onSortChange"
            @page-change="onPageChange"
            @per-page-change="onPerPageChange"
            @page-change-from-bv="onPageChangeFromBv"
            @view-detail="handleViewDetail"
          />

          <StructuredLearningModal
            :modal-id="'structured-learning-modal'"
            :data="modalData"
            :is-loading="isModalLoading"
            :can-review="!isOwnLearning(modalData)"
            @close="showModal = false"
            @complete="handleComplete"
            @revise="handleRevise"
          />

          <modal-filter
            v-model="filters"
            @apply-filters="applyFilters"
            @reset-filters="resetFilters"
            :training-type-options="trainingTypes"
            :competency-options="competencies"
            :training-method-options="trainingMethods"
          />

          <ConfirmationSubmitModal
            :showConfirmationSubmit="showConfirmationSubmitModal"
            @close="showConfirmationSubmitModal = false"
            :data="modalData"
            :isSubmitting="isSubmitting"
            @submit="completeLearning"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import logic from "./main.logic";
import Header from "./components/header.vue";
import Summary from "./components/summary.vue";
import StructuredLearningModal from "./modal/structured-learning-modal.vue";
import ModalFilter from "./modal/filter-modal.vue";
import CertificateReviewTable from "./table/certificate-review-table.vue";
import ConfirmationSubmitModal from "./modal/confirmation-submit-modal.vue";

export default {
  name: "CertificateReviewPage",
  components: {
    Header,
    Summary,
    StructuredLearningModal,
    ConfirmationSubmitModal,
    CertificateReviewTable,
    ModalFilter,
  },
  mixins: [logic],
};
</script>

<style scoped>
.filter-panel {
  background: #fafafa;
  border: 1px solid #edf0f2;
  border-radius: 8px;
  padding: 18px;
}

.filter-label {
  color: #6c757d;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

</style>
